import { Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/dramas';

export default function About() {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tiantiancinema.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://tiantiancinema.com/about' },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '天天影院',
    url: 'https://tiantiancinema.com',
    description: '天天影视为您整理最新电视剧全集、国产剧全集在线、韩剧全集免费、美剧全集播放与日剧全集资源，追剧无需等待，全部剧集一网打尽。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '光华路88号',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-8888',
      contactType: 'customer service',
      email: 'contact@tiantiancinema.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 天天影院 | 品牌介绍与联系方式"
        description="天天影院品牌介绍，了解我们的使命与愿景。天天影视为您整理最新电视剧全集、国产剧全集在线、韩剧全集免费、美剧全集播放与日剧全集资源。"
        keywords="天天影院,关于天天影院,天天影院品牌,联系方式"
        canonicalUrl="https://tiantiancinema.com/about"
        ogTitle="关于我们 - 天天影院"
        ogDescription="天天影院品牌介绍，了解我们的使命与愿景。"
        ogUrl="https://tiantiancinema.com/about"
        schemaObjects={[breadcrumbSchema, orgSchema]}
      />

      <div className="w-full px-4 md:px-6 pt-24 pb-14">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              关于天天影院
            </h1>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 mb-10">
              <h2 className="text-lg font-bold text-foreground-950 mb-4">品牌介绍</h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  天天影院成立于2020年，是一家专注于为华语观众提供优质电视剧资源的在线平台。我们致力于汇聚全球优质剧集内容，覆盖<strong>国产剧</strong>、<strong>韩剧</strong>、<strong>美剧</strong>、<strong>日剧</strong>等多个品类，让每一位追剧爱好者都能在这里找到心仪的作品。
                </p>
                <p>
                  我们的团队由一群热爱影视内容的年轻人组成，我们深知追剧人的需求——更新要快、分类要全、体验要好。因此，我们不断优化平台的资源整合能力，确保<strong>最新电视剧全集</strong>第一时间上线，让用户追剧无需等待。
                </p>
                <p>
                  天天影院始终坚持"追剧无需等待"的品牌理念，无论是<strong>国产剧全集在线</strong>观看，还是<strong>韩剧全集免费</strong>资源，亦或是<strong>美剧全集播放</strong>和<strong>日剧全集资源</strong>，我们都力求做到一网打尽，让用户享受到一站式的追剧体验。
                </p>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 mb-10">
              <h2 className="text-lg font-bold text-foreground-950 mb-4">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-flashlight-line', title: '极速更新', desc: '新剧上线24小时内同步更新，追剧快人一步' },
                  { icon: 'ri-stack-line', title: '资源齐全', desc: '国产剧、韩剧、美剧、日剧全覆盖，一网打尽' },
                  { icon: 'ri-shield-check-line', title: '高清画质', desc: '全部剧集支持高清播放，部分支持超高清画质' },
                  { icon: 'ri-device-line', title: '多端适配', desc: '电脑、平板、手机完美适配，随时随地追剧' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3 p-4 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1 whitespace-nowrap">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-lg font-bold text-foreground-950 mb-4">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <span className="text-sm font-semibold text-foreground-950 block whitespace-nowrap">{t.name}</span>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                    <span className="text-xs text-foreground-400 mt-3 block">{t.date}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950 mb-4">联系我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg">
                    <i className="ri-mail-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500 block">电子邮箱</span>
                    <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">contact@tiantiancinema.com</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg">
                    <i className="ri-phone-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500 block">客服热线</span>
                    <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">400-888-8888</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-secondary-100 rounded-lg">
                    <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500 block">公司地址</span>
                    <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">北京市朝阳区光华路88号</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页开始追剧
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}